import { useState } from 'react'
import { X, Save } from 'lucide-react'
import { format } from 'date-fns'

const ValuationModal = ({ isOpen, vehicle, onClose, onSave, loading = false }) => {
  const [formData, setFormData] = useState({
    valuation_date: format(new Date(), 'yyyy-MM-dd'),
    estimated_value: '',
    source: 'personal_estimate',
    notes: ''
  })

  const [errors, setErrors] = useState({})

  const sourceOptions = [
    { value: 'personal_estimate', label: 'Personal Estimate' },
    { value: 'dealer_quote', label: 'Dealer Quote' },
    { value: 'auction_result', label: 'Auction Result' },
    { value: 'hagerty', label: 'Hagerty' },
    { value: 'bring_a_trailer', label: 'Bring a Trailer' },
    { value: 'other', label: 'Other' }
  ]

  const validateForm = () => {
    const newErrors = {}

    if (!formData.estimated_value || parseFloat(formData.estimated_value) <= 0) {
      newErrors.estimated_value = 'Please enter a valid estimated value'
    }

    if (!formData.valuation_date) {
      newErrors.valuation_date = 'Please select a valuation date'
    }

    if (!formData.source) {
      newErrors.source = 'Please select a valuation source'
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = (e) => {
    e.preventDefault()

    if (!validateForm()) {
      return
    }

    onSave({
      ...formData,
      estimated_value: parseFloat(formData.estimated_value)
    })
  }

  const handleChange = (e) => {
    const { name, value } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: value
    }))
    // Clear error for this field
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }))
    }
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-rdc-cream">
          <h2 className="font-display text-xl font-semibold text-black">
            Update Valuation
          </h2>
          <button
            onClick={onClose}
            className="text-rdc-taupe hover:text-black transition-colors"
          >
            <X size={24} />
          </button>
        </div>

        {/* Vehicle Info */}
        <div className="p-6 bg-rdc-cream border-b border-rdc-warm-gray/30">
          <p className="text-xs text-rdc-taupe mb-1">Vehicle</p>
          <p className="font-medium text-black">
            {vehicle?.year} {vehicle?.make} {vehicle?.model}
          </p>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {/* Valuation Date */}
          <div>
            <label className="block text-sm font-medium text-black mb-2">
              Valuation Date *
            </label>
            <input
              type="date"
              name="valuation_date"
              value={formData.valuation_date}
              onChange={handleChange}
              max={format(new Date(), 'yyyy-MM-dd')}
              className={`w-full px-4 py-2 border border-rdc-cream rounded-lg focus:outline-none focus:ring-2 focus:ring-rdc-primary ${
                errors.valuation_date ? 'border-red-500' : ''
              }`}
            />
            {errors.valuation_date && (
              <p className="text-red-500 text-xs mt-1">{errors.valuation_date}</p>
            )}
          </div>

          {/* Estimated Value */}
          <div>
            <label className="block text-sm font-medium text-black mb-2">
              Estimated Value ($) *
            </label>
            <input
              type="number"
              name="estimated_value"
              value={formData.estimated_value}
              onChange={handleChange}
              placeholder="e.g., 45000"
              min="0"
              step="100"
              className={`w-full px-4 py-2 border border-rdc-cream rounded-lg focus:outline-none focus:ring-2 focus:ring-rdc-primary ${
                errors.estimated_value ? 'border-red-500' : ''
              }`}
            />
            {errors.estimated_value && (
              <p className="text-red-500 text-xs mt-1">{errors.estimated_value}</p>
            )}
            {formData.estimated_value && (
              <p className="text-xs text-rdc-taupe mt-1">
                ${parseFloat(formData.estimated_value).toLocaleString('en-US', {
                  minimumFractionDigits: 0,
                  maximumFractionDigits: 0
                })}
              </p>
            )}
          </div>

          {/* Source */}
          <div>
            <label className="block text-sm font-medium text-black mb-2">
              Valuation Source *
            </label>
            <select
              name="source"
              value={formData.source}
              onChange={handleChange}
              className={`w-full px-4 py-2 border border-rdc-cream rounded-lg focus:outline-none focus:ring-2 focus:ring-rdc-primary ${
                errors.source ? 'border-red-500' : ''
              }`}
            >
              {sourceOptions.map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
            {errors.source && (
              <p className="text-red-500 text-xs mt-1">{errors.source}</p>
            )}
          </div>

          {/* Notes */}
          <div>
            <label className="block text-sm font-medium text-black mb-2">
              Notes (Optional)
            </label>
            <textarea
              name="notes"
              value={formData.notes}
              onChange={handleChange}
              placeholder="e.g., Condition excellent, recent service, minor wear..."
              rows="4"
              className="w-full px-4 py-2 border border-rdc-cream rounded-lg focus:outline-none focus:ring-2 focus:ring-rdc-primary resize-none"
            />
            <p className="text-xs text-rdc-taupe mt-1">
              {formData.notes.length}/500
            </p>
          </div>

          {/* Help Text */}
          <div className="bg-rdc-cream rounded-lg p-4">
            <p className="text-xs text-rdc-taupe">
              <strong>Tip:</strong> Use comparable sales data, dealer estimates, or valuation guides like Hagerty, NADA, or Bring a Trailer to determine market value.
            </p>
          </div>

          {/* Buttons */}
          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 border border-rdc-primary text-rdc-primary rounded-lg hover:bg-rdc-primary/5 transition-colors font-medium"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex-1 btn-primary flex items-center justify-center gap-2"
            >
              <Save size={18} />
              {loading ? 'Saving...' : 'Save Valuation'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

export default ValuationModal
